"use client";

import { useSelector } from "react-redux";

import type { RootState } from "@/store/store";
import { isRenderableLeadFormConfig, type PublicLeadFormConfig } from "@/types/leadForm";

/**
 * The custom lead form this page should render on a given surface — or null.
 *
 * ─── Why the surface check matters ──────────────────────────────────────────────────
 * `form_type` is locked at university level and says which SURFACE a config is designed
 * for. Without this check an inline host would happily render a config authored as a
 * popup — the popup skin tree inside a 400px page column — and the popup host would render
 * an inline one. Both look like a broken form rather than a wrong setting, which is the
 * hardest kind of bug to report.
 *
 * Returning null is always safe: the caller falls back to the legacy variant, which is
 * what renders today everywhere.
 *
 * `null` covers all of: no config resolved for this page, a config with no steps (an
 * authoring state, not a form), and a config meant for the other surface.
 */
export function useResolvedLeadForm(surface: "fixed" | "popup"): PublicLeadFormConfig | null {
  // Each surface has its OWN resolved slot — the server resolves them independently
  // (page pin → course → university default), so there is nothing to filter here beyond
  // confirming the form is actually renderable.
  const inline = useSelector((state: RootState) => state.leaddropform.lead_form_config);
  const popup = useSelector((state: RootState) => state.leaddropform.lead_form_popup_config);
  const config = surface === "popup" ? popup : inline;
  if (!isRenderableLeadFormConfig(config)) return null;
  // Belt and braces: a row whose form_type disagrees with the slot it arrived in is a
  // server bug, and rendering it would put the wrong layout on screen.
  const configSurface = config.form_type === "popup" ? "popup" : "fixed";
  return configSurface === surface ? config : null;
}
